import { Alert, Button, Modal, Spin, Tag } from "@arco-design/web-react";
import { BadgeCheck, Calculator, ReceiptText, RotateCcw, UserRound, Wallet } from "lucide-react";

import type { FeeAdjustmentRecord } from "../types";
import { useFeeAdjustmentRecordDetail } from "../runtime";
import { formatFeeAdjustmentCompactAmount, sumFeeAdjustmentAmounts } from "../service";
import { FeeAdjustmentServiceTable } from "./FeeAdjustmentServiceTable";
import { FeeAdjustmentMemberTable } from "./FeeAdjustmentMemberTable";
import "./feeAdjustmentRecordDetail.css";

/** 使用已保存的金额快照展示方向，不参与 R3 或销售金额计算。 */
function Amount({ value, signed = true }: { value?: string | null; signed?: boolean }) {
  const formatted = formatFeeAdjustmentCompactAmount(value, signed);
  const direction = formatted.startsWith("+") ? "increase" : formatted.startsWith("−") ? "decrease" : "same";
  return <span className="fee-record-amount" data-direction={signed ? direction : "same"}>
    {formatted}
  </span>;
}

/** 公共只读费用详情，完整呈现需求、成本、R3 分摊和逐项个人加价。 */
export function FeeAdjustmentRecordDetail({ record }: { record: FeeAdjustmentRecord }) {
  const processed = record.status === "processed";
  const payers = record.members.filter((member) => member.is_payer);
  const nonPayers = record.members.filter((member) => !member.is_payer);
  const r3Total = record.members.length
    ? sumFeeAdjustmentAmounts(record.members.map((member) => member.cost_allocation_amount))
    : null;
  return <div className="fee-record-detail">
    <header className="fee-record-overview">
      <span className="fee-record-overview-icon"><ReceiptText size={24} aria-hidden="true" /></span>
      <div><div className="fee-record-eyebrow">费用调整记录</div><h2>{record.request_id.slice(0, 8).toUpperCase()}</h2><p>{record.members.length} 位成员<span>·</span>{record.lines.length} 项服务</p></div>
      <Tag color={processed ? "green" : "arcoblue"}>{processed ? "计调处理完成" : "销售发起费用调整申请"}</Tag>
    </header>
    <section className="fee-record-request" aria-label="需求与处理依据">
      <div className="fee-record-request-sales"><h3><UserRound size={16} aria-hidden="true" />调整需求</h3><div className="fee-record-author"><strong>{record.created_by_name || "销售"}</strong><time>{new Date(record.created_at).toLocaleString("zh-CN", { hour12: false })}</time></div><p>{record.request_text}</p></div>
      <div className="fee-record-request-planner"><h3><BadgeCheck size={16} aria-hidden="true" />计调处理依据</h3><div className="fee-record-author"><strong>{record.processed_by_name || "计调"}</strong>{record.processed_at && <time>{new Date(record.processed_at).toLocaleString("zh-CN", { hour12: false })}</time>}</div><p>{record.processing_basis || "尚未填写"}</p></div>
    </section>
    <section className="fee-record-participants" aria-label="出钱成员范围">
      <span>出钱成员<strong>{payers.length}</strong>人</span>
      <span>不出钱成员<strong>{nonPayers.length}</strong>人</span>
    </section>
    {!processed ? <Alert type="info" content="计调尚未完成，暂无费用与分摊结果" /> : <>
      <dl className="fee-record-totals">
        <div><dt><ReceiptText size={16} aria-hidden="true" />本次成本变化</dt><dd><Amount value={record.total_cost_delta_amount} /></dd></div>
        <div><dt><Calculator size={16} aria-hidden="true" />R3 总金额</dt><dd><Amount value={r3Total} /></dd></div>
        <div><dt><Wallet size={16} aria-hidden="true" />人员应收变化</dt><dd><Amount value={record.total_receivable_delta_amount} /></dd></div>
        <div><dt><BadgeCheck size={16} aria-hidden="true" />利润变化</dt><dd><Amount value={record.total_profit_delta_amount} /></dd></div>
      </dl>
      <section className="fee-record-section">
        <h3>费用调整明细 <span>{record.lines.length} 项</span></h3>
        <FeeAdjustmentServiceTable lines={record.lines} amountKind="r3" label="费用成本与R3明细" markupMembers={record.members} compactAmounts showUnitPrice={false} />
      </section>
      <section className="fee-record-section">
        <h3>成员分摊与加价 <span>{record.members.length} 人</span></h3>
        <FeeAdjustmentMemberTable record={record} label="成员分摊与加价" />
      </section>
    </>}
  </div>;
}

/** 在保留来源列表位置的同时提供独立、可滚动的费用详情阅读空间。 */
export function FeeAdjustmentRecordDetailModal({ target, onClose }: { target: { workspaceId: string; requestId: string } | null; onClose: () => void }) {
  return <Modal visible={Boolean(target)} title="费用调整详情"
    style={{ width: "min(1280px, calc(100vw - 32px))" }} className="fee-record-modal"
    alignCenter footer={null} onCancel={onClose} unmountOnExit>
    {target && <RecordDetailQuery key={`${target.workspaceId}:${target.requestId}`} {...target} />}
  </Modal>;
}

/** 查询失败优先于缓存，避免权限失效或删除后继续展示旧记录。 */
function RecordDetailQuery({ workspaceId, requestId }: { workspaceId: string; requestId: string }) {
  const query = useFeeAdjustmentRecordDetail(workspaceId, requestId);
  if (query.isFetching || query.fetchStatus === "paused") return <div className="fee-record-loading" role="status"><Spin size={28} />正在加载费用调整详情</div>;
  if (query.isError) return <Alert type="error" content="费用调整详情加载失败" action={<Button type="text" icon={<RotateCcw size={14} />} onClick={() => void query.refetch()}>重试</Button>} />;
  return query.data ? <FeeAdjustmentRecordDetail record={query.data} /> : null;
}
